/**
 * HelpProvider - Global context for tooltip/help system
 *
 * Provides:
 * - Access to tooltip content from i18n
 * - Registry metadata lookup
 * - User role filtering
 * - Onboarding tour integration
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type {
  TooltipMeta,
  TooltipContent,
  TooltipComplexity,
} from '@/content/help/schema';
import { TRIGGER_CONFIG, ACCESSIBILITY_CONFIG } from '@/content/help/schema';
import {
  getTooltipMeta,
  getOnboardingTooltips,
  getPageHelpEntry,
  getMetricHelpEntry,
  type TooltipId,
  type MetricId,
} from '@/content/help/registry';
import type { PageId, PageHelpEntry, MetricHelpEntry } from '@/content/help/schema';

interface HelpContextValue {
  /**
   * Get tooltip content by ID
   */
  getTooltip: (id: TooltipId) => {
    meta: TooltipMeta | undefined;
    content: TooltipContent | undefined;
  };

  /**
   * Get trigger config for complexity level
   */
  getTriggerConfig: (
    complexity: TooltipComplexity
  ) => (typeof TRIGGER_CONFIG)[TooltipComplexity];

  /**
   * Get accessibility config
   */
  accessibilityConfig: typeof ACCESSIBILITY_CONFIG;

  /**
   * Check if tooltip should be visible for current user role
   */
  isVisibleForRole: (id: TooltipId) => boolean;

  /**
   * Get onboarding tour steps
   */
  getOnboardingSteps: (tour: 'admin' | 'agent' | 'supervisor') => TooltipMeta[];

  /**
   * Get page-level help entry by page ID
   */
  getPageHelp: (pageId: PageId) => PageHelpEntry;

  /**
   * Get metric help entry by metric ID
   */
  getMetricHelp: (metricId: MetricId) => MetricHelpEntry;

  /**
   * Current user role (for filtering)
   */
  userRole: TooltipMeta['roles'][number] | null;
}

const HelpContext = React.createContext<HelpContextValue | null>(null);

interface HelpProviderProps {
  children: React.ReactNode;
  /**
   * Current user role for filtering tooltips
   */
  userRole?: TooltipMeta['roles'][number] | null;
}

export function HelpProvider({ children, userRole = null }: HelpProviderProps) {
  const { t } = useTranslation(['common', 'settings']);

  const getTooltip = React.useCallback(
    (id: TooltipId) => {
      const meta = getTooltipMeta(id);

      // Get content from i18n
      // ID format: "settings.organization.region" -> t("tooltips.settings.organization.region")
      const i18nKey = `tooltips.${id}`;

      const content: TooltipContent | undefined = {
        title: t(`${i18nKey}.title`, { defaultValue: '' }) || undefined,
        body: t(`${i18nKey}.body`, { defaultValue: '' }),
        details: t(`${i18nKey}.details`, { defaultValue: '' }) || undefined,
        example: t(`${i18nKey}.example`, { defaultValue: '' }) || undefined,
      };

      // Only return content if body exists
      if (!content.body) {
        return { meta, content: undefined };
      }

      return { meta, content };
    },
    [t]
  );

  const getTriggerConfig = React.useCallback((complexity: TooltipComplexity) => {
    return TRIGGER_CONFIG[complexity];
  }, []);

  const isVisibleForRole = React.useCallback(
    (id: TooltipId) => {
      const meta = getTooltipMeta(id);
      if (!meta) return false;

      // Empty roles array means visible to all
      if (meta.roles.length === 0) return true;

      // If no user role provided, show all tooltips
      if (!userRole) return true;

      return meta.roles.includes(userRole);
    },
    [userRole]
  );

  const getOnboardingSteps = React.useCallback(
    (tour: 'admin' | 'agent' | 'supervisor') => {
      return getOnboardingTooltips(tour);
    },
    []
  );

  const getPageHelp = React.useCallback((pageId: PageId): PageHelpEntry => {
    return getPageHelpEntry(pageId);
  }, []);

  const getMetricHelp = React.useCallback((metricId: MetricId): MetricHelpEntry => {
    return getMetricHelpEntry(metricId);
  }, []);

  const value = React.useMemo<HelpContextValue>(
    () => ({
      getTooltip,
      getTriggerConfig,
      accessibilityConfig: ACCESSIBILITY_CONFIG,
      isVisibleForRole,
      getOnboardingSteps,
      getPageHelp,
      getMetricHelp,
      userRole,
    }),
    [
      getTooltip,
      getTriggerConfig,
      isVisibleForRole,
      getOnboardingSteps,
      getPageHelp,
      getMetricHelp,
      userRole,
    ]
  );

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

/**
 * Hook to access help system
 */
export function useHelp() {
  const context = React.useContext(HelpContext);
  if (!context) {
    throw new Error('help_provider.context_missing');
  }
  return context;
}

/**
 * Hook to get specific tooltip data
 */
export function useTooltip(id: TooltipId) {
  const { getTooltip, getTriggerConfig, isVisibleForRole } = useHelp();

  return React.useMemo(() => {
    const { meta, content } = getTooltip(id);
    const isVisible = isVisibleForRole(id);
    const triggerConfig = meta ? getTriggerConfig(meta.complexity) : null;

    return {
      meta,
      content,
      isVisible,
      triggerConfig,
    };
  }, [id, getTooltip, getTriggerConfig, isVisibleForRole]);
}
